import {
  Form,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from "@/components/ui";
import { IEdoCoursePost } from "@/shared/model/edo-course-post.model";
import { IEdoWallPost, defaultValue } from "@/shared/model/edo-wall-post.model";
import { getFileAvatar } from "@/shared/util/file-avatar-utils";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Translate } from "react-jhipster";
import { z } from "zod";
import { cn } from "@/shared/util/cn";

function EditPostDescriptionField() {
  const { control } = useFormContext();
  return (
    <Controller
      name="description"
      control={control}
      render={({ field, fieldState }) => (
        <div className="w-full">
          <label className="mb-1 block text-sm font-semibold text-[var(--color-mockup-text)]">
            Descrizione
          </label>
          <textarea
            {...field}
            rows={4}
            className={cn(
              "flex w-full rounded-2xl border-2 border-[var(--color-mockup-border)] bg-surface px-4 py-3 text-sm font-editorial text-[var(--color-mockup-text)] focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary",
              fieldState.error && "border-[var(--color-mockup-coral)] focus:ring-[var(--color-mockup-coral)]"
            )}
          />
          {fieldState.error?.message && (
            <p className="mt-1 text-sm text-[var(--color-mockup-coral)]">
              {fieldState.error.message}
            </p>
          )}
        </div>
      )}
    />
  );
}

export interface EditPostProps {
  post?: IEdoWallPost | IEdoCoursePost;
  open: boolean;
  onClose: () => void;
  loading: boolean;
  updating: boolean;
  updateSuccess: boolean;
  updatePost: (post: IEdoWallPost | IEdoCoursePost) => void;
}

export const EditPost = (props: EditPostProps) => {
  const [form, setForm] = useState<IEdoWallPost>({
    ...defaultValue,
    ...props.post,
  });

  const {
    post,
    open,
    onClose: handleClose,
    loading,
    updating,
    updatePost,
  } = props;

  useEffect(() => {
    setForm({ ...defaultValue, ...post });
  }, [post]);

  const saveEntityLocal = async (data: { description: string }) => {
    const payload = { ...form, description: data.description };
    await updatePost(payload);
    handleClose();
  };

  const descriptionSchema = z.object({
    description: z.string().min(1, "Required"),
  });

  const removeFile = (index: number) => {
    const files = Array.from(form.files || []);
    files.splice(index, 1);
    setForm({ ...form, files });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Modifica il post</DialogTitle>
        </DialogHeader>
        <p className="mb-4 text-body text-[var(--color-mockup-text-mid)]">
          Modifica il contenuto del post.
        </p>
        {loading ? (
          <div className="flex justify-center py-4">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        ) : (
          <Form
            id="edit-post-form"
            schema={descriptionSchema}
            onSubmit={saveEntityLocal}
            defaultValues={{ description: form.description || "" }}
            className="flex flex-col gap-4"
          >
            <EditPostDescriptionField />
            {form.files?.length ? (
              <div>
                <p className="mb-2 text-sm font-semibold text-[var(--color-mockup-text)]">
                  File
                </p>
                <List className="flex flex-col divide-y divide-[var(--color-mockup-border)]">
                  {form.files.map((file, index) => (
                    <ListItem
                      key={file.id}
                      className="flex items-center gap-3 py-2"
                    >
                      <div className="mr-3 shrink-0">
                        {getFileAvatar(file.fileType)}
                      </div>
                      <ListItemText primary={file.originalFileName} />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => removeFile(index)}
                          aria-label="Rimuovi file"
                        >
                          <X className="h-4 w-4" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </div>
            ) : null}
          </Form>
        )}
        <DialogFooter>
          <Button variant="outline-solid" onClick={handleClose}>
            <Translate contentKey="entity.action.cancel">Cancel</Translate>
          </Button>
          <Button
            type="submit"
            form="edit-post-form"
            disabled={updating}
          >
            <Translate contentKey="entity.action.save" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
